const INTEGER_LIMIT = 2147483647;

const CURRENCY_REGEX = {
  DEFAULT_CURRENCY: /\B(?=(?:\d{3})+(?!\d))/g,
  INR_CURRENCY: /\B(?=(?:(\d\d)+(\d)(?!\d))+(?!\d))/g,
  SORT_CODE: /\B(?=(?:\d{2})+(?!\d))/g,
};

type CurrencyType = keyof typeof CURRENCY_REGEX;

export interface NumericFormatOptions {
  currencyType?: CurrencyType;
  digitPlaces?: number;
  decimalPlaces?: number;
  spreadDigitCharacter?: string;
  spreadDecimalCharacter?: string;
  unsigned?: boolean;
  allowLeadingZero?: boolean;
  minValue?: number;
  maxValue?: number;
}

export const integerFormatOptions: NumericFormatOptions = {
  currencyType: 'DEFAULT_CURRENCY',
  digitPlaces: 10,
  decimalPlaces: 0,
  spreadDigitCharacter: ',',
  spreadDecimalCharacter: '',
  unsigned: true,
};

export const accountNumberFormatOptions: NumericFormatOptions = {
  digitPlaces: 8,
  decimalPlaces: 0,
  spreadDigitCharacter: '',
  spreadDecimalCharacter: '',
  unsigned: true,
};

export const currencyFormatOptions: NumericFormatOptions = {
  currencyType: 'DEFAULT_CURRENCY',
  digitPlaces: 10,
  decimalPlaces: 2,
  spreadDigitCharacter: ',',
  spreadDecimalCharacter: '.',
  unsigned: true,
};

export const negativeCurrencyFormatOptions: NumericFormatOptions = {
  ...currencyFormatOptions,
  unsigned: false,
};

export const sortCodeFormatOptions: NumericFormatOptions = {
  currencyType: 'SORT_CODE',
  digitPlaces: 6,
  spreadDigitCharacter: '-',
  unsigned: true,
  allowLeadingZero: true,
};

export const percentageFormatOptions: NumericFormatOptions = {
  ...currencyFormatOptions,
  digitPlaces: 3,
  decimalPlaces: 2,
};

export const twoDigitPercentageFormatOptions: NumericFormatOptions = {
  ...percentageFormatOptions,
  digitPlaces: 2,
};

const limitValue = (v: string, minValue?: number, maxValue?: number) => {
  if (minValue && parseInt(v, 10) < minValue) return minValue.toString();
  if (maxValue && parseInt(v, 10) > maxValue) return maxValue.toString();
  return v;
};

export const formatNumeric = (v: string, {
  currencyType,
  digitPlaces,
  decimalPlaces,
  spreadDigitCharacter,
  spreadDecimalCharacter,
  unsigned = true,
  allowLeadingZero = false,
  minValue,
  maxValue,
}: NumericFormatOptions = {}): string => {
  const value = limitValue(v, minValue, maxValue);

  // Split integer digit part and decimal part
  const [valueBlock, decimalBlock] = (value ?? '').split('.');

  // eslint-disable-next-line no-undef
  let removedLeadingZerosValueBlock = allowLeadingZero ? valueBlock : valueBlock.replace(/^0+/g, '');

  // Keep only one first zero
  if (removedLeadingZerosValueBlock === '' && valueBlock.includes('0')) {
    removedLeadingZerosValueBlock = '0';
  }

  // Check negative number?
  const isNegativeAtFirstIndex = !unsigned && removedLeadingZerosValueBlock.startsWith('-');

  // Check user input decimals?
  const isUserInputDot = decimalBlock !== undefined;

  // Handle a format for value block
  const numberOnlyValueBlock = removedLeadingZerosValueBlock?.replace(/\D+/g, '') ?? '';
  const formattedValueBlock = numberOnlyValueBlock.slice(0, digitPlaces).replace(CURRENCY_REGEX[currencyType ?? 'DEFAULT_CURRENCY'], spreadDigitCharacter ?? '');

  // Handle a format for decimal block
  const formattedDecimalBlock = (decimalBlock?.replace(/\D+/g, '') ?? '').slice(0, decimalPlaces ?? undefined);

  // Return final formatted number
  return `${isNegativeAtFirstIndex ? '-' : ''}${formattedValueBlock}${isUserInputDot ? (spreadDecimalCharacter ?? '') + formattedDecimalBlock : ''}`;
};

export const formatNumericOnBlur = (value: string, options: NumericFormatOptions = {}): string => {
  if (!value) {
    return value;
  }

  if (value === '-') {
    return '';
  }

  if (value.endsWith('.')) {
    return value.slice(0, -1);
  }

  const [valueBlock, decimalBlock] = (value ?? '').split('.');
  const count = (options.decimalPlaces ?? 0) - (decimalBlock?.length ?? 0);

  return formatNumeric(`${valueBlock}${options.spreadDecimalCharacter ?? ''}${decimalBlock || ''}${'0'.repeat(count > 0 ? count : 0)}`, options);
};

export const convertToNumber = (input: string) => (input ? Number(input.replace(/[^0-9.-]+/g, '')).toFixed(2) : 0);

export const addLeadingZero = (v: string) => (v?.length === 1 && Number(v) > 0 && Number(v) < 10 ? `0${v}` : v);
export const withinIntegerLimit = (input: string) => Number(input || 0) <= INTEGER_LIMIT;
